class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.deferred = this.hasAttribute('data-deferred');
    this.form = this.querySelector('form');
    this.countryInput = this.querySelector('input[name="country_code"]');
    this.languageInput = this.querySelector('input[name="language_code"]');

    if (this.deferred && this.form) {
      this.form.addEventListener('submit', () => {
        document.cookie = 'location-popup=hidden; path=/; SameSite=Lax';
      });
    }

    this.groups = Array.from(this.querySelectorAll('.disclosure')).map((disclosure) => {
      const button = disclosure.querySelector('.disclosure__button');
      const panel = disclosure.querySelector('.disclosure__list');
      const role = disclosure.dataset.select;

      button.addEventListener('click', () => this.openSelector(button, panel));
      button.addEventListener('focusout', (event) => this.closeSelector(event, disclosure, button, panel));
      panel.querySelectorAll('a').forEach((item) => {
        item.addEventListener('click', (event) => this.onItemClick(event, role));
      });

      return { disclosure, button, panel, role };
    });

    this.addEventListener('keyup', this.onContainerKeyUp.bind(this));
  }

  getGroup(role) {
    return this.groups.find((group) => group.role === role);
  }

  findLanguageItem(group, code) {
    const items = Array.from(group.panel.querySelectorAll('a'));
    const exact = items.find((item) => item.dataset.value === code);
    if (exact) return exact;

    // Fall back to a matching base language (e.g. "en-US" -> "en") if the exact locale isn't offered.
    const base = code.split('-')[0].toLowerCase();
    return items.find((item) => item.dataset.value.split('-')[0].toLowerCase() === base) || null;
  }

  hidePanel(group) {
    group.button.setAttribute('aria-expanded', 'false');
    group.panel.setAttribute('hidden', true);
  }

  onContainerKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openGroup = this.groups.find((group) => !group.panel.hasAttribute('hidden'));
    if (openGroup) {
      this.hidePanel(openGroup);
      openGroup.button.focus();
    }
  }

  setSelected(group, item) {
    group.panel.querySelectorAll('a[aria-current]').forEach((el) => el.removeAttribute('aria-current'));
    item.setAttribute('aria-current', 'true');

    const caret = group.button.querySelector('svg');
    group.button.innerHTML = item.innerHTML;
    if (caret) {
      group.button.appendChild(caret);
    }
  }

  onItemClick(event, role) {
    event.preventDefault();
    const link = event.currentTarget;
    const group = this.getGroup(role);

    // Hide the location popup for the rest of this browser session.
    document.cookie = 'location-popup=hidden; path=/; SameSite=Lax';

    if (link.hasAttribute('data-alternate-store')) {
      const redirectUrl = link.getAttribute('href');
      if (redirectUrl) {
        window.location.href = redirectUrl;
      }
      return;
    }

    if (role === 'country' && this.countryInput) {
      this.countryInput.value = link.dataset.value;
    } else if (this.languageInput) {
      this.languageInput.value = link.dataset.value;
    }

    if (group) {
      this.setSelected(group, link);
      this.hidePanel(group);
    }

    // Selecting a country updates the suggested language, but leaves it overridable.
    if (role === 'country' && link.dataset.languageValue && this.languageInput) {
      const languageGroup = this.getGroup('language');

      if (languageGroup) {
        const languageItem = this.findLanguageItem(languageGroup, link.dataset.languageValue);
        // Only update if the suggested language is actually offered; otherwise keep the current selection.
        if (languageItem) {
          this.languageInput.value = languageItem.dataset.value;
          this.setSelected(languageGroup, languageItem);
        }
      } else {
        this.languageInput.value = link.dataset.languageValue;
      }
    }

    if (!this.deferred && this.form) {
      this.form.submit();
    }
  }

  openSelector(button, panel) {
    button.focus();
    panel.toggleAttribute('hidden');
    button.setAttribute('aria-expanded', (button.getAttribute('aria-expanded') === 'false').toString());
  }

  closeSelector(event, disclosure, button, panel) {
    const shouldClose = event.relatedTarget && !disclosure.contains(event.relatedTarget);
    if (event.relatedTarget === null || shouldClose) {
      button.setAttribute('aria-expanded', 'false');
      panel.setAttribute('hidden', true);
    }
  }
}

if (!customElements.get('localization-form')) {
  customElements.define('localization-form', LocalizationForm);
}